// controllers/card_animations_controller.js
import { Controller } from "@hotwired/stimulus"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
gsap.registerPlugin(ScrollTrigger)

export default class extends Controller {
    static targets = ["trendyCard", "trendyContainer"]

    connect() {
        // visible by default (safety)
        this.trendyCardTargets.forEach(el => {
            el.style.opacity = "1"
            el.style.transform = "none"
        })

        // Build after images + next tick (iOS layout settle)
        this._init().then(() => {
            this._build()
            ScrollTrigger.refresh()
        })

        this._onResize = () => ScrollTrigger.refresh()
        window.addEventListener("resize", this._onResize, { passive: true })
        window.addEventListener("orientationchange", this._onResize)

        // Watchdog: if nothing has fired after 2s, reveal everything
        this._revealTimer = setTimeout(() => this._revealAll(), 2000)
    }

    disconnect() {
        clearTimeout(this._revealTimer)
        window.removeEventListener("resize", this._onResize)
        window.removeEventListener("orientationchange", this._onResize)
        ScrollTrigger.getAll().forEach(t => t.kill())
    }

    async _init() {
        const imgs = Array.from(this.element.querySelectorAll("img"))
        await Promise.all(imgs.map(img => img.complete ? Promise.resolve()
            : new Promise(res => img.addEventListener("load", res, { once: true }))))
        await new Promise(r => setTimeout(r, 0))
    }

    _build() {
        const cards = this.trendyCardTargets
        if (!cards.length) return

        const triggerEl = this.hasTrendyContainerTarget
            ? this.trendyContainerTarget
            : this.element

        // Mobile: smaller motion, play once, animate FROM so default is visible
        const isMobile = window.matchMedia("(max-width: 767.98px)").matches

        // Middle first (index 1) if it exists
        const middle = cards[1]
        if (middle) {
            gsap.from(middle, {
                y: isMobile ? 80 : 240,
                autoAlpha: 0,
                duration: isMobile ? 0.7 : 1.1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: triggerEl,
                    start: isMobile ? "top 92%" : "top 85%",
                    end: "bottom 60%",
                    once: isMobile,                // important for iOS
                    invalidateOnRefresh: true,
                    onEnter: () => clearTimeout(this._revealTimer),
                }
            })
        }

        // Side cards next
        const sides = cards.filter((_, i) => i !== 1)
        if (sides.length) {
            gsap.from(sides, {
                y: isMobile ? 80 : 240,
                autoAlpha: 0,
                duration: isMobile ? 0.7 : 1.1,
                ease: "power3.out",
                stagger: isMobile ? 0.08 : 0.12,
                scrollTrigger: {
                    trigger: triggerEl,
                    start: isMobile ? "top 92%" : "top 85%",
                    end: "bottom 60%",
                    once: isMobile,
                    invalidateOnRefresh: true,
                    onEnter: () => clearTimeout(this._revealTimer),
                }
            })
        }
    }

    _revealAll() {
        // Safety fallback: ensure visible if triggers didn’t fire
        this.trendyCardTargets.forEach(el => {
            el.style.opacity = "1"
            el.style.transform = "none"
        })
    }
}